/**
 * Admin service for multi-tenant admin dashboard and user management
 */

import { apiService } from './apiService';
import {
  AdminUser,
  AuditLog,
  SupportTicket,
  SupportTicketMessage,
  AdminEmailConfig,
  SystemConfig,
  SecurityEvent,
  AdminDashboardResponse,
  UserManagementResponse,
  SystemMetricsResponse,
  AdminUserCreateRequest,
  AdminUserUpdateRequest,
  SupportTicketUpdateRequest,
  AdminEmailConfigCreateRequest,
  AdminEmailConfigUpdateRequest,
  SystemConfigCreateRequest,
  SystemConfigUpdateRequest,
  SecurityEventCreateRequest,
  SecurityEventUpdateRequest,
  UserFilters,
  AuditLogFilters,
  SupportTicketFilters,
  SecurityEventFilters,
  MarketingLead,
  MarketingLeadFilters,
  MarketingSubscription,
  MarketingSubscriptionFilters,
  AdminErrorResponse
} from '../types/admin';

class AdminService {
  private baseUrl = '/api/v1/admin';

  // Admin Dashboard Endpoints
  async getDashboard(): Promise<AdminDashboardResponse> {
    try {
      const response = await apiService.get<AdminDashboardResponse>(`${this.baseUrl}/dashboard`);
      return response.data;
    } catch (error) {
      console.error('Error getting admin dashboard:', error);
      throw this.handleError(error);
    }
  }

  async getSystemMetrics(metricName?: string, hours: number = 24): Promise<SystemMetricsResponse> {
    try {
      const params = new URLSearchParams();
      if (metricName) params.append('metric_name', metricName);
      params.append('hours', hours.toString());
      
      const response = await apiService.get<SystemMetricsResponse>(`${this.baseUrl}/metrics?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error getting system metrics:', error);
      throw this.handleError(error);
    }
  }

  async getSystemHealth(): Promise<any> {
    try {
      const response = await apiService.get<any>(`${this.baseUrl}/health`);
      return response.data;
    } catch (error) {
      console.error('Error getting system health:', error);
      throw this.handleError(error);
    }
  }

  async getSystemOverview(): Promise<any> {
    try {
      const response = await apiService.get<any>(`${this.baseUrl}/overview`);
      return response.data;
    } catch (error) {
      console.error('Error getting system overview:', error);
      throw this.handleError(error);
    }
  }

  // User Management Endpoints
  async getUsers(filters?: UserFilters): Promise<UserManagementResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.subscription_tier) params.append('subscription_tier', filters.subscription_tier);
      if (filters?.page) params.append('skip', ((filters.page - 1) * (filters.page_size || 100)).toString());
      if (filters?.page_size) params.append('limit', filters.page_size.toString());
      
      const response = await apiService.get<UserManagementResponse>(`${this.baseUrl}/users?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error getting users:', error);
      throw this.handleError(error);
    }
  }

  async getUserDetails(userId: number): Promise<any> {
    try {
      const response = await apiService.get(`${this.baseUrl}/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting user details:', error);
      throw this.handleError(error);
    }
  }

  async updateUser(userId: number, updateData: Record<string, any>): Promise<void> {
    try {
      await apiService.put(`${this.baseUrl}/users/${userId}`, updateData);
    } catch (error) {
      console.error('Error updating user:', error);
      throw this.handleError(error);
    }
  }

  async suspendUser(userId: number): Promise<void> {
    try {
      await apiService.post(`${this.baseUrl}/users/${userId}/suspend`);
    } catch (error) {
      console.error('Error suspending user:', error);
      throw this.handleError(error);
    }
  }

  async activateUser(userId: number): Promise<void> {
    try {
      await apiService.post(`${this.baseUrl}/users/${userId}/activate`);
    } catch (error) {
      console.error('Error activating user:', error);
      throw this.handleError(error);
    }
  }

  async getUserActivity(userId: number, page: number = 1, pageSize: number = 100): Promise<any> {
    try {
      const skip = (page - 1) * pageSize;
      const response = await apiService.get(`${this.baseUrl}/users/${userId}/activity?skip=${skip}&limit=${pageSize}`);
      return response.data;
    } catch (error) {
      console.error('Error getting user activity:', error);
      throw this.handleError(error);
    }
  }

  // System Monitoring Endpoints
  async getSystemStatus(): Promise<any> {
    try {
      const response = await apiService.get(`${this.baseUrl}/system/status`);
      return response.data;
    } catch (error) {
      console.error('Error getting system status:', error);
      throw this.handleError(error);
    }
  }

  async getSystemLogs(filters?: AuditLogFilters): Promise<AuditLog[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.action) params.append('action', filters.action);
      if (filters?.resource_type) params.append('resource_type', filters.resource_type);
      if (filters?.user_id) params.append('user_id', filters.user_id.toString());
      if (filters?.admin_id) params.append('admin_id', filters.admin_id.toString());
      if (filters?.page) params.append('skip', ((filters.page - 1) * (filters.page_size || 100)).toString());
      if (filters?.page_size) params.append('limit', filters.page_size.toString());
      
      const response = await apiService.get<{ logs: any[] }>(`${this.baseUrl}/system/logs?${params}`);
      return response.data.logs;
    } catch (error) {
      console.error('Error getting system logs:', error);
      throw this.handleError(error);
    }
  }

  async getSystemAlerts(): Promise<any> {
    try {
      const response = await apiService.get(`${this.baseUrl}/system/alerts`);
      return response.data;
    } catch (error) {
      console.error('Error getting system alerts:', error);
      throw this.handleError(error);
    }
  }

  // Customer Support Endpoints
  async getSupportTickets(filters?: SupportTicketFilters): Promise<SupportTicket[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.priority) params.append('priority', filters.priority);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.page) params.append('skip', ((filters.page - 1) * (filters.page_size || 100)).toString());
      if (filters?.page_size) params.append('limit', filters.page_size.toString());
      
      const response = await apiService.get<SupportTicket[]>(`${this.baseUrl}/support/tickets?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error getting support tickets:', error);
      throw this.handleError(error);
    }
  }

  async getSupportTicket(ticketId: string): Promise<SupportTicket> {
    try {
      const response = await apiService.get<SupportTicket>(`${this.baseUrl}/support/tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting support ticket:', error);
      throw this.handleError(error);
    }
  }

  async updateSupportTicket(ticketId: string, updateData: SupportTicketUpdateRequest): Promise<SupportTicket> {
    try {
      const response = await apiService.put<SupportTicket>(`${this.baseUrl}/support/tickets/${ticketId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating support ticket:', error);
      throw this.handleError(error);
    }
  }

  /** Assign a ticket to an admin user, or pass null to unassign. */
  async assignSupportTicket(ticketId: string, assignedTo: string | null): Promise<SupportTicket> {
    try {
      const response = await apiService.post<SupportTicket>(
        `${this.baseUrl}/support/tickets/${ticketId}/assign`,
        { assigned_to: assignedTo }
      );
      return response.data;
    } catch (error) {
      console.error('Error assigning support ticket:', error);
      throw this.handleError(error);
    }
  }

  /** Fetch the message thread for a ticket (oldest first). */
  async getTicketMessages(ticketId: string): Promise<SupportTicketMessage[]> {
    try {
      const response = await apiService.get<{ messages?: SupportTicketMessage[] }>(
        `${this.baseUrl}/support/tickets/${ticketId}/messages`
      );
      return response.data?.messages ?? [];
    } catch (error) {
      console.error('Error getting ticket messages:', error);
      throw this.handleError(error);
    }
  }

  /** Post an admin reply to a ticket. Use isInternal for notes not shown to the user. */
  async addTicketMessage(
    ticketId: string,
    messageContent: string,
    isInternal = false
  ): Promise<SupportTicketMessage> {
    try {
      const response = await apiService.post<SupportTicketMessage>(
        `${this.baseUrl}/support/tickets/${ticketId}/messages`,
        { message_content: messageContent, is_internal: isInternal }
      );
      return response.data;
    } catch (error) {
      console.error('Error adding ticket message:', error);
      throw this.handleError(error);
    }
  }

  async getSupportAnalytics(): Promise<any> {
    try {
      const response = await apiService.get(`${this.baseUrl}/support/analytics`);
      return response.data;
    } catch (error) {
      console.error('Error getting support analytics:', error);
      throw this.handleError(error);
    }
  }

  // Audit and Security Endpoints
  async getAuditLogs(filters?: AuditLogFilters): Promise<{
    logs: AuditLog[];
    total_count: number;
    page: number;
    page_size: number;
  }> {
    try {
      const params = new URLSearchParams();
      if (filters?.action) params.append('action', filters.action);
      if (filters?.resource_type) params.append('resource_type', filters.resource_type);
      if (filters?.user_id) params.append('user_id', filters.user_id.toString());
      if (filters?.admin_id) params.append('admin_id', filters.admin_id.toString());
      if (filters?.page) params.append('skip', ((filters.page - 1) * (filters.page_size || 50)).toString());
      if (filters?.page_size) params.append('limit', filters.page_size.toString());
      
      const response = await apiService.get<{
        logs?: AuditLog[];
        total_count?: number;
        page?: number;
        page_size?: number;
      }>(`${this.baseUrl}/audit/logs?${params}`);
      const data = response.data;
      if (Array.isArray(data)) {
        return {
          logs: data,
          total_count: data.length,
          page: filters?.page ?? 1,
          page_size: filters?.page_size ?? 50,
        };
      }
      return {
        logs: data?.logs ?? [],
        total_count: data?.total_count ?? 0,
        page: data?.page ?? filters?.page ?? 1,
        page_size: data?.page_size ?? filters?.page_size ?? 50,
      };
    } catch (error) {
      console.error('Error getting audit logs:', error);
      throw this.handleError(error);
    }
  }

  async getSecurityEvents(filters?: SecurityEventFilters): Promise<SecurityEvent[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.severity) params.append('severity', filters.severity);
      if (filters?.resolved !== undefined) params.append('resolved', filters.resolved.toString());
      if (filters?.event_type) params.append('event_type', filters.event_type);
      if (filters?.page) params.append('skip', ((filters.page - 1) * (filters.page_size || 100)).toString());
      if (filters?.page_size) params.append('limit', filters.page_size.toString());
      
      const response = await apiService.get<SecurityEvent[]>(`${this.baseUrl}/security/events?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error getting security events:', error);
      throw this.handleError(error);
    }
  }

  async getSecurityIncidents(): Promise<any> {
    try {
      const response = await apiService.get(`${this.baseUrl}/security/incidents`);
      return response.data;
    } catch (error) {
      console.error('Error getting security incidents:', error);
      throw this.handleError(error);
    }
  }

  async createSecurityIncident(incidentData: SecurityEventCreateRequest): Promise<void> {
    try {
      await apiService.post(`${this.baseUrl}/security/incidents`, incidentData);
    } catch (error) {
      console.error('Error creating security incident:', error);
      throw this.handleError(error);
    }
  }

  async updateSecurityEvent(
    eventId: string | number,
    updateData: SecurityEventUpdateRequest
  ): Promise<SecurityEvent> {
    try {
      const response = await apiService.put<SecurityEvent>(
        `${this.baseUrl}/security/events/${eventId}`,
        {
          resolved: updateData.resolved,
          is_resolved: updateData.resolved,
          resolution_notes: updateData.resolution_notes,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating security event:', error);
      throw this.handleError(error);
    }
  }

  // Admin User Management (for super admins)
  async createAdminUser(adminData: AdminUserCreateRequest): Promise<AdminUser> {
    try {
      const response = await apiService.post<AdminUser>(`${this.baseUrl}/admin-users`, adminData);
      return response.data;
    } catch (error) {
      console.error('Error creating admin user:', error);
      throw this.handleError(error);
    }
  }

  async getAdminUsers(): Promise<AdminUser[]> {
    try {
      const response = await apiService.get<AdminUser[]>(`${this.baseUrl}/admin-users`);
      return response.data;
    } catch (error) {
      console.error('Error getting admin users:', error);
      throw this.handleError(error);
    }
  }

  async updateAdminUser(adminId: number, updateData: AdminUserUpdateRequest): Promise<AdminUser> {
    try {
      const response = await apiService.put<AdminUser>(`${this.baseUrl}/admin-users/${adminId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating admin user:', error);
      throw this.handleError(error);
    }
  }

  async deleteAdminUser(adminId: number): Promise<void> {
    try {
      await apiService.delete(`${this.baseUrl}/admin-users/${adminId}`);
    } catch (error) {
      console.error('Error deleting admin user:', error);
      throw this.handleError(error);
    }
  }

  // Email Configuration Management
  async createAdminEmailConfig(configData: AdminEmailConfigCreateRequest): Promise<AdminEmailConfig> {
    try {
      const response = await apiService.post<AdminEmailConfig>(`${this.baseUrl}/email-configs`, configData);
      return response.data;
    } catch (error) {
      console.error('Error creating admin email config:', error);
      throw this.handleError(error);
    }
  }

  async getAdminEmailConfigs(): Promise<AdminEmailConfig[]> {
    try {
      const response = await apiService.get<AdminEmailConfig[]>(`${this.baseUrl}/email-configs`);
      return response.data;
    } catch (error) {
      console.error('Error getting admin email configs:', error);
      throw this.handleError(error);
    }
  }

  async updateAdminEmailConfig(configId: number, updateData: AdminEmailConfigUpdateRequest): Promise<AdminEmailConfig> {
    try {
      const response = await apiService.put<AdminEmailConfig>(`${this.baseUrl}/email-configs/${configId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating admin email config:', error);
      throw this.handleError(error);
    }
  }

  async deleteAdminEmailConfig(configId: number): Promise<void> {
    try {
      await apiService.delete(`${this.baseUrl}/email-configs/${configId}`);
    } catch (error) {
      console.error('Error deleting admin email config:', error);
      throw this.handleError(error);
    }
  }

  // System Configuration Management
  async createSystemConfig(configData: SystemConfigCreateRequest): Promise<SystemConfig> {
    try {
      const response = await apiService.post<SystemConfig>(`${this.baseUrl}/system-configs`, configData);
      return response.data;
    } catch (error) {
      console.error('Error creating system config:', error);
      throw this.handleError(error);
    }
  }

  async getSystemConfigs(): Promise<SystemConfig[]> {
    try {
      const response = await apiService.get<SystemConfig[]>(`${this.baseUrl}/system-configs`);
      return response.data;
    } catch (error) {
      console.error('Error getting system configs:', error);
      throw this.handleError(error);
    }
  }

  async updateSystemConfig(configId: number, updateData: SystemConfigUpdateRequest): Promise<SystemConfig> {
    try {
      const response = await apiService.put<SystemConfig>(`${this.baseUrl}/system-configs/${configId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating system config:', error);
      throw this.handleError(error);
    }
  }

  async deleteSystemConfig(configId: number): Promise<void> {
    try {
      await apiService.delete(`${this.baseUrl}/system-configs/${configId}`);
    } catch (error) {
      console.error('Error deleting system config:', error);
      throw this.handleError(error);
    }
  }

  async getMarketingLeads(filters?: MarketingLeadFilters): Promise<{
    leads: MarketingLead[];
    total_count: number;
    page: number;
    page_size: number;
  }> {
    try {
      const params = new URLSearchParams();
      if (filters?.email) params.append('email', filters.email);
      if (filters?.source) params.append('source', filters.source);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.page_size) params.append('page_size', filters.page_size.toString());

      const response = await apiService.get<{
        leads: MarketingLead[];
        total_count: number;
        page: number;
        page_size: number;
      }>(`${this.baseUrl}/marketing-leads?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error getting marketing leads:', error);
      throw this.handleError(error);
    }
  }

  async getMarketingSubscriptions(filters?: MarketingSubscriptionFilters): Promise<{
    subscriptions: MarketingSubscription[];
    total_count: number;
    page: number;
    page_size: number;
  }> {
    try {
      const params = new URLSearchParams();
      if (filters?.email) params.append('email', filters.email);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.page_size) params.append('page_size', filters.page_size.toString());

      const response = await apiService.get<{
        subscriptions: MarketingSubscription[];
        total_count: number;
        page: number;
        page_size: number;
      }>(`${this.baseUrl}/marketing-subscriptions?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error getting marketing subscriptions:', error);
      throw this.handleError(error);
    }
  }

  async updateMarketingSubscriptionStatus(
    subscriptionId: number,
    status: 'subscribed' | 'unsubscribed'
  ): Promise<MarketingSubscription> {
    try {
      const response = await apiService.post<MarketingSubscription>(
        `${this.baseUrl}/marketing-subscriptions/${subscriptionId}/status`,
        { status }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating marketing subscription:', error);
      throw this.handleError(error);
    }
  }

  // Error handling
  private handleError(error: any): AdminErrorResponse {
    if (error.response?.data) {
      return error.response.data;
    }
    
    return {
      error: 'Unknown Error',
      message: error.message || 'An unexpected error occurred',
      timestamp: new Date().toISOString(),
      request_id: '',
      tenant_id: ''
    };
  }
}

export const adminService = new AdminService();
